// src/utils/markdownSections.js
/**
 * Markdown을 "헤더 섹션 우선(+ 없으면 문단)" 단위로 쪼개기 위한 유틸.
 *
 * 섹션 정의(우선):
 * - level >= 2 헤더(##, ###, ...)부터 시작
 * - "다음 동일/상위 레벨 헤더(level <= currentLevel)" 전까지를 1 섹션으로 간주
 * - 하위 레벨(더 많은 #) 헤더는 섹션 내부에 포함
 *
 * 폴백(헤더가 거의 없을 때):
 * - 빈 줄 기준 문단 단위로 섹션 생성
 */

const HEADING_RE = /^(#{2,6})\s+(.*)\s*$/;

function buildLineOffsets(lines) {
  const offsets = new Array(lines.length + 1);
  let acc = 0;
  for (let i = 0; i < lines.length; i++) {
    offsets[i] = acc;
    // 원본 줄바꿈은 split("\n")로 떨어졌다고 가정 -> 각 라인 뒤에 '\n' 1개가 있었다고 계산
    acc += lines[i].length + 1;
  }
  offsets[lines.length] = acc;
  return offsets;
}

function slugifyTitle(title) {
  return (title || "")
    .toString()
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^\w\-가-힣]/g, "")
    .slice(0, 60);
}

export function parseMarkdownSections(markdown) {
  const text = markdown || "";
  const lines = text.split("\n");
  const offsets = buildLineOffsets(lines);

  // 헤더 목록 수집 (##~######)
  const headings = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(HEADING_RE);
    if (!m) continue;
    const level = m[1].length;
    const title = (m[2] || "").trim();
    headings.push({ line: i, level, title });
  }

  // 헤더가 없으면 문단 폴백
  const hasSectionHeadings = headings.some((h) => h.level >= 2);
  if (!hasSectionHeadings) {
    const sections = [];
    let startLine = 0;
    let idx = 0;
    const flush = (endLine) => {
      const slice = lines.slice(startLine, endLine).join("\n").trimEnd();
      if (!slice.trim()) return;
      const start = offsets[startLine];
      const end = offsets[endLine];
      sections.push({
        id: `p-${idx++}`,
        kind: "paragraph",
        title: `문단 ${idx}`,
        level: null,
        start,
        end,
        markdown: slice,
      });
    };

    for (let i = 0; i <= lines.length; i++) {
      const isBreak = i === lines.length || (lines[i] ?? "").trim() === "";
      if (isBreak) {
        flush(i);
        startLine = i + 1;
      }
    }
    return sections;
  }

  // 헤더 섹션 생성
  const sections = [];

  // 첫 섹션 헤더(##) 이전의 프리앰블도 섹션으로 (제목/서문이 있을 수 있음)
  const first = headings[0];
  if (first && first.line > 0) {
    const pre = lines.slice(0, first.line).join("\n").trimEnd();
    if (pre.trim()) {
      sections.push({
        id: "preamble",
        kind: "preamble",
        title: null,
        level: null,
        start: 0,
        end: offsets[first.line],
        markdown: pre,
      });
    }
  }

  // 각 헤더를 섹션 시작점으로 보고 end 계산
  for (let i = 0; i < headings.length; i++) {
    const h = headings[i];
    if (h.level < 2) continue;

    // 다음 동일/상위 레벨 헤더를 찾는다 (level <= h.level)
    let endLine = lines.length;
    for (let j = i + 1; j < headings.length; j++) {
      const next = headings[j];
      if (next.level <= h.level && next.level >= 2) {
        endLine = next.line;
        break;
      }
    }

    const slice = lines.slice(h.line, endLine).join("\n").trimEnd();
    const start = offsets[h.line];
    const end = offsets[endLine];
    const titleSlug = slugifyTitle(h.title);
    sections.push({
      id: `h${h.level}-${h.line}-${titleSlug || "section"}`,
      kind: "section",
      title: h.title || null,
      level: h.level,
      start,
      end,
      markdown: slice,
    });
  }

  return sections;
}

